/**
 * FoodPulseHealthStrip — cross-site block that pulls Food Pulse Health
 * articles into any Social Pulse page (used on /health).
 *
 * Rationale: the Nutrition & Food side of health (Nutrition, Diabetes,
 * Heart Health, Gut Health, etc.) is authored on Food Pulse. Rather
 * than duplicating the copy on Social Pulse, we mirror the list here
 * and deep-link to `/food-pulse/health/:topic` so readers get the full
 * article without leaving the network. Links stay same-tab so the
 * Header/Footer swap to the Food Pulse brand feels seamless.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, HeartPulse, Salad } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TOPIC_TO_SLUG = {
  "all":                 "",              // → /food-pulse/health (hub)
  "nutrition":           "nutrition",
  "healthy-eating":      "healthy-eating",
  "weight-management":   "weight-management",
  "diabetes":            "diabetes",
  "heart-health":        "heart-health",
  "childrens-nutrition": "childrens-nutrition",
  "senior-nutrition":    "senior-nutrition",
  "allergies":           "allergies",
  "safety":              "safety",
  "supplements":         "supplements",
  // Drill-downs (added later)
  "cholesterol":         "cholesterol",
  "blood-pressure":      "blood-pressure",
  "gut-health":          "gut-health",
  "sleep-nutrition":     "sleep-nutrition",
};

export default function FoodPulseHealthStrip({ limit = 8, showAllHub = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-articles`, {
          params: { category: "health", limit: limit + 4 },
        });
        if (cancelled) return;
        // Sort so the "all" hub entry is first (if present), then alphabetical.
        const list = (res.data.items || []).slice();
        list.sort((a, b) => {
          if (a.tag === "all") return -1;
          if (b.tag === "all") return 1;
          return (a.title || "").localeCompare(b.title || "");
        });
        setItems(list);
      } catch {
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section className="mb-10" data-testid="sp-health-foodpulse-strip">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-2xl inline-flex items-center gap-2">
            <Salad className="w-5 h-5" style={{ color: "#B25D38" }} />
            Nutrition & Food Health
          </h2>
          <div className="text-xs opacity-70 mt-1">
            Long-form nutrition articles — authored on Food Pulse, mirrored here for Social Pulse readers.
          </div>
        </div>
        <Link
          to="/food-pulse/health"
          className="text-sm inline-flex items-center gap-0.5 hover:underline"
          style={{ color: "#B25D38" }}
          data-testid="sp-health-foodpulse-strip-all"
        >
          All on Food Pulse <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.slice(0, limit).map((a) => {
          const topicSlug = TOPIC_TO_SLUG[a.tag] || a.tag;
          const to = topicSlug === ""
            ? "/food-pulse/health"
            : `/food-pulse/health/${topicSlug}`;
          const isHub = a.tag === "all";
          if (isHub && !showAllHub) return null;
          return (
            <Link
              key={a.slug}
              to={to}
              className="wofy-card !p-4 hover:shadow-md transition block"
              style={{ borderLeft: "3px solid #B25D38" }}
              data-testid={`sp-health-fp-card-${a.tag}`}
            >
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1 inline-flex items-center gap-1">
                <HeartPulse className="w-3 h-3" />
                {isHub ? "Food Pulse Health · Hub" : "Food Pulse · Health"}
              </div>
              <div className="font-medium leading-snug mb-1">{a.title}</div>
              {a.excerpt && (
                <div className="text-xs opacity-75 line-clamp-2">{a.excerpt}</div>
              )}
              <div className="mt-2 text-xs inline-flex items-center gap-1" style={{ color: "#B25D38" }}>
                Read on Food Pulse <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
